import { el, qs } from '../utils/dom.js';

/**
 * Auth UI — status bar indicator + user menu.
 * Sign-in/sign-up redirects to Logto hosted page (no modal form needed).
 */
export class AuthUI {
  constructor(auth, cloudStorage) {
    this._auth = auth;
    this._cloudStorage = cloudStorage || null;

    this._indicator = el('span', {
      className: 'auth-indicator',
      onClick: () => this._handleIndicatorClick(),
    });
    this._updateIndicator();

    const statusBar = qs('#status-bar');
    const dirtyEl = qs('#status-dirty');
    if (statusBar && dirtyEl) {
      statusBar.insertBefore(this._indicator, dirtyEl);
    } else if (statusBar) {
      statusBar.appendChild(this._indicator);
    }

    auth.onAuthChange(() => this._updateIndicator());
  }

  _updateIndicator() {
    const user = this._auth.getUser();
    if (user) {
      this._indicator.textContent = user.displayName || user.email;
      this._indicator.title = `${user.email} (${this._auth.getTier()})`;
      this._indicator.classList.add('signed-in');
    } else {
      this._indicator.textContent = 'Sign In';
      this._indicator.title = 'Sign in with EchoForge account';
      this._indicator.classList.remove('signed-in');
    }
  }

  _handleIndicatorClick() {
    if (this._auth.isAuthenticated()) {
      this._showUserMenu();
    } else {
      this._auth.signIn();
    }
  }

  _showUserMenu() {
    const user = this._auth.getUser();
    const tier = this._auth.getTier();

    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog', style: { minWidth: '320px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'EchoForge Account'));

    const info = el('div', { style: { marginBottom: '16px' } });
    info.appendChild(el('div', { style: { fontSize: '14px', fontWeight: '600', marginBottom: '2px' } }, user.displayName));
    info.appendChild(el('div', { style: { fontSize: '12px', color: 'var(--text-dim)' } }, user.email));

    const tierBadge = el('span', {
      className: `tier-badge tier-${tier}`,
    }, tier.charAt(0).toUpperCase() + tier.slice(1));
    info.appendChild(tierBadge);
    dialog.appendChild(info);

    // Storage usage (if cloud storage available)
    if (this._cloudStorage?.isOnline) {
      const storageInfo = el('div', { style: { fontSize: '11px', color: 'var(--text-dim)', marginBottom: '12px' } });
      storageInfo.textContent = 'Loading storage info...';
      this._cloudStorage.list().then(result => {
        if (result.totalCloudSize !== undefined) {
          storageInfo.textContent = `Cloud storage: ${(result.totalCloudSize / 1024 / 1024).toFixed(1)} MB used`;
        }
      }).catch(() => {});
      dialog.appendChild(storageInfo);
    }

    const btnRow = el('div', { className: 'dialog-buttons' });
    btnRow.appendChild(el('button', { className: 'btn', onClick: async () => {
      overlay.remove();
      await this._auth.signOut();
    }}, 'Sign Out'));
    btnRow.appendChild(el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Close'));
    dialog.appendChild(btnRow);

    const portalLink = el('div', { style: { textAlign: 'center', marginTop: '8px', fontSize: '11px' } });
    const link = el('a', { href: 'https://auth.echoforge.games', target: '_blank', style: { color: 'var(--accent)' } });
    link.textContent = 'Manage account';
    portalLink.appendChild(link);
    dialog.appendChild(portalLink);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  /** Open the sign-in flow (public entry point for session expiry re-auth). */
  showSignIn() {
    this._auth.signIn();
  }

  /**
   * Show auth dialog — kept for backward compatibility with WelcomeScreen.
   * Both 'signin' and 'signup' modes redirect to the Logto hosted page.
   */
  _showAuthDialog(_mode) {
    this._auth.signIn();
  }
}
